import React, { useState } from 'react';
import styled from 'styled-components';
import { Globe, ChevronDown, Check } from 'lucide-react';
import { colors, spacing, typography, borderRadius, shadows } from '../styles/theme';
import { useLocale } from '../locales/useI18n';
import { useAnalytics } from '../hooks/useAnalytics';
import type { Locale } from '../locales/index';

interface LocaleOption {
  code: Locale;
  name: string;
  nativeName: string;
  flag: string;
  region: string;
}

const localeOptions: LocaleOption[] = [
  {
    code: 'en-US',
    name: 'English (US)',
    nativeName: 'English (United States)',
    flag: '🇺🇸',
    region: 'North America',
  },
  {
    code: 'en-CA',
    name: 'English (CA)',
    nativeName: 'English (Canada)',
    flag: '🇨🇦',
    region: 'North America',
  },
  {
    code: 'fr-CA',
    name: 'Français (CA)',
    nativeName: 'Français (Canada)',
    flag: '🇨🇦',
    region: 'North America',
  },
  {
    code: 'es-419',
    name: 'Español (LATAM)',
    nativeName: 'Español (Latinoamérica)',
    flag: '🌎',
    region: 'Latin America',
  },
  {
    code: 'pt-BR',
    name: 'Português (BR)',
    nativeName: 'Português (Brasil)',
    flag: '🇧🇷',
    region: 'South America',
  },
];

interface LocaleSwitcherProps {
  showFullName?: boolean;
  compact?: boolean;
}

const Container = styled.div`
  position: relative;
  display: inline-block;
`;

const TriggerButton = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'compact',
})<{ compact?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${props => props.compact ? spacing[2] : spacing[3]};
  padding: ${props => props.compact ? `${spacing[2]} ${spacing[3]}` : `${spacing[3]} ${spacing[4]}`};
  background: ${colors.white};
  border: 1px solid ${colors.border.normal};
  border-radius: ${borderRadius.lg};
  color: ${colors.text.primary};
  font-size: ${typography.fontSizes.sm};
  font-weight: ${typography.fontWeights.medium};
  cursor: pointer;
  transition: all 0.15s ease;
  user-select: none;

  &:hover {
    border-color: ${colors.border.focus};
    background: ${colors.background.secondary};
  }

  &:focus {
    outline: 2px solid ${colors.primary[500]};
    outline-offset: 2px;
    border-color: ${colors.primary[500]};
  }

  &:active {
    transform: translateY(1px);
  }
`;

const Flag = styled.span`
  font-size: ${typography.fontSizes.lg};
  line-height: 1;
`;

const LocaleName = styled.span.withConfig({
  shouldForwardProp: (prop) => prop !== 'compact',
})<{ compact?: boolean }>`
  ${props => props.compact && `
    @media (max-width: 768px) {
      display: none;
    }
  `}
`;

const ChevronIcon = styled(ChevronDown).withConfig({
  shouldForwardProp: (prop) => prop !== 'isOpen',
})<{ isOpen: boolean }>`
  width: 16px;
  height: 16px;
  color: ${colors.text.tertiary};
  transition: transform 0.15s ease;
  transform: rotate(${props => props.isOpen ? 180 : 0}deg);
`;

const Dropdown = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isOpen',
})<{ isOpen: boolean }>`
  position: absolute;
  top: calc(100% + ${spacing[1]});
  right: 0;
  min-width: 240px;
  background: ${colors.white};
  border: 1px solid ${colors.border.normal};
  border-radius: ${borderRadius.lg};
  box-shadow: ${shadows.lg};
  z-index: 1000;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transform: translateY(${props => props.isOpen ? 0 : -8}px);
  transition: all 0.15s ease;
  max-height: 300px;
  overflow-y: auto;
`;

const DropdownSection = styled.div`
  padding: ${spacing[2]} 0;
  
  &:not(:last-child) {
    border-bottom: 1px solid ${colors.border.light};
  }
`;

const SectionTitle = styled.div`
  padding: ${spacing[2]} ${spacing[4]} ${spacing[1]} ${spacing[4]};
  font-size: ${typography.fontSizes.xs};
  font-weight: ${typography.fontWeights.semibold};
  color: ${colors.text.tertiary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DropdownItem = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  padding: ${spacing[3]} ${spacing[4]};
  background: ${props => props.isActive ? colors.primary[50] : 'transparent'};
  border: none;
  color: ${colors.text.primary};
  font-size: ${typography.fontSizes.sm};
  text-align: left;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background: ${props => props.isActive ? colors.primary[100] : colors.background.secondary};
  }

  &:focus {
    outline: none;
    background: ${colors.primary[50]};
  }
`;

const ItemFlag = styled.span`
  font-size: ${typography.fontSizes.base};
  line-height: 1;
  min-width: 20px;
`;

const ItemContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const ItemName = styled.div`
  font-weight: ${typography.fontWeights.medium};
  color: ${colors.text.primary};
`;

const ItemNativeName = styled.div`
  font-size: ${typography.fontSizes.xs};
  color: ${colors.text.secondary};
  margin-top: ${spacing[1]};
`;

const CheckIcon = styled(Check)`
  width: 16px;
  height: 16px;
  color: ${colors.primary[600]};
  opacity: ${props => props.style?.opacity || 1};
`;

const Overlay = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isOpen',
})<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999;
  display: ${props => props.isOpen ? 'block' : 'none'};
`;

export const LocaleSwitcher: React.FC<LocaleSwitcherProps> = ({ 
  showFullName = true, 
  compact = false 
}) => {
  const { locale, setLocale } = useLocale();
    const { trackEvent } = useAnalytics();
  const [isOpen, setIsOpen] = useState(false);

  const currentLocale = localeOptions.find(option => option.code === locale);
  
  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleLocaleSelect = (newLocale: Locale) => {
    trackEvent({ category: 'settings', action: 'locale_change', custom_parameters: { action_location: 'locale_switcher', outcome: newLocale } });
    setLocale(newLocale);
    setIsOpen(false);
  };

  const handleOverlayClick = () => {
    setIsOpen(false);
  };

  // Group locales by region
  const groupedLocales = localeOptions.reduce((groups, option) => {
    const region = option.region;
    if (!groups[region]) {
      groups[region] = [];
    }
    groups[region].push(option);
    return groups;
  }, {} as Record<string, LocaleOption[]>);

  return (
    <>
      <Overlay isOpen={isOpen} onClick={handleOverlayClick} />
      <Container>
        <TriggerButton 
          onClick={handleToggle}
          compact={compact}
          aria-label="Select language"
          aria-expanded={isOpen}
          aria-haspopup="true"
        >
          {!compact && <Globe size={16} />}
          <Flag>{currentLocale?.flag}</Flag>
          {showFullName && <LocaleName compact={compact}>{currentLocale?.name}</LocaleName>}
          <ChevronIcon isOpen={isOpen} />
        </TriggerButton>

        <Dropdown isOpen={isOpen} role="menu">
          {Object.entries(groupedLocales).map(([region, options]) => (
            <DropdownSection key={region}>
              <SectionTitle>{region}</SectionTitle>
              {options.map((option) => (
                <DropdownItem
                  key={option.code}
                  isActive={option.code === locale}
                  onClick={() => handleLocaleSelect(option.code)}
                  role="menuitem"
                >
                  <ItemFlag>{option.flag}</ItemFlag>
                  <ItemContent>
                    <ItemName>{option.name}</ItemName>
                    <ItemNativeName>{option.nativeName}</ItemNativeName>
                  </ItemContent>
                  {option.code === locale && <CheckIcon />}
                </DropdownItem>
              ))}
            </DropdownSection>
          ))}
        </Dropdown>
      </Container>
    </>
  );
};

export default LocaleSwitcher;